import { useCallback } from 'react';

import { Agreement } from '@m/types';
import { apiFetchV2 } from '@m/utils/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// 'Do not sell' agreements are excluded everywhere agreements are auto- or user-agreed to —
// they're opt-out, not opt-in, so accepting T&C/Privacy elsewhere should never imply this too.
const DNS_AGREEMENT_TYPE_ID = 3;

interface GetUnagreedAgreementsResponse {
  agreements: Agreement[];
}

function clearUnagreedAgreements(oldData: GetUnagreedAgreementsResponse | undefined) {
  return oldData ? { ...oldData, agreements: [] } : oldData;
}

const getCurrentAgreement = async (agreementType: number) =>
  apiFetchV2<{ agreement: Agreement }>(`/agreements/${agreementType}/current`);

const getUnagreedAgreements = async () =>
  apiFetchV2<GetUnagreedAgreementsResponse>('/customer/agreements');

const agreeToAgreement = async ({
  agreementType,
  agreementId,
}: {
  agreementId: number;
  agreementType: number;
}) =>
  apiFetchV2<{ acceptanceId: number }>(`/customer/agreement/${agreementType}/${agreementId}`, {
    method: 'POST',
  });

export const useCurrentAgreement = (agreementType: number) =>
  useQuery({
    queryKey: ['agreements', 'current', agreementType],
    queryFn: () => getCurrentAgreement(agreementType),
    select: (data) => data.agreement,
    enabled: !!agreementType,
  });

export const useUnagreedAgreements = () =>
  useQuery({
    queryKey: ['agreements', 'unagreed'],
    queryFn: () => getUnagreedAgreements(),
    select: (data) =>
      data.agreements.filter((agreement: Agreement) => agreement.type.id !== DNS_AGREEMENT_TYPE_ID),
  });

export const useAgreeToMultipleAgreements = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (agreements: Agreement[]) => {
      const results = await Promise.allSettled(
        agreements.map((agreement) =>
          agreeToAgreement({
            agreementId: agreement.id,
            agreementType: agreement.type.id,
          }),
        ),
      );
      return results;
    },
    onMutate: () => {
      queryClient.setQueryData(['agreements', 'unagreed'], clearUnagreedAgreements);
    },
    onError: () => {
      // Don't rollback the optimistic update if the calls fail.
      // The next time the user loads the page the sheet will appear since they haven't agreed yet (calls failed).
      // This is an edge case and the calls will likely not fail but provides a better UX if they do.
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agreements', 'unagreed'] });
    },
  });
};

/** Records agreements already disclosed and accepted by an onboarding screen. */
export function useAutoAgreeToPendingAgreements() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    const { agreements } = await getUnagreedAgreements();
    const pending = agreements.filter((agreement) => agreement.type.id !== DNS_AGREEMENT_TYPE_ID);
    if (pending.length === 0) return;

    queryClient.setQueryData(['agreements', 'unagreed'], clearUnagreedAgreements);
    try {
      await Promise.all(
        pending.map((agreement) =>
          agreeToAgreement({
            agreementId: agreement.id,
            agreementType: agreement.type.id,
          }),
        ),
      );
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['agreements', 'unagreed'] });
    }
  }, [queryClient]);
}
